import { Clerk } from '@clerk/clerk-js'
import { Capacitor } from '@capacitor/core'
import { ActionFunction, ActionFunctionArgs, redirect } from 'react-router-dom'
import { parseUserLimits } from '@schemas'
import { fetchClerk, refreshClerkUser, waitForClerkUser } from '@utils/loaders'

type AuthenticatedActionFunction<Context = unknown, ReturnData = ReturnType<ActionFunction>> = (
    args: ActionFunctionArgs<Context>,
    Clerk: Clerk,
) => ReturnData

type LimitedActionFunction<Context = unknown, ReturnData = ReturnType<ActionFunction>> = (
    args: ActionFunctionArgs<Context>,
    Clerk: Clerk,
    userLimits: UserLimits,
) => ReturnData

interface LimitedActionConfig<LimitKey extends keyof UserLimits> {
    limit: LimitKey
    getRedirectPath: (args: ActionFunctionArgs, Clerk: Clerk, userLimits: UserLimits) => string
}

export const createAction = <Action = ActionFunction>(action: Action) => action
export const createAuthenticatedAction = <AuthenticatedAction extends AuthenticatedActionFunction>(action: AuthenticatedAction) => {
    return createAction(async (args) => {
        const Clerk = await fetchClerk()
        if (!Clerk.user && Capacitor.isNativePlatform()) {
            await waitForClerkUser(Clerk)
        }

        if (!Clerk.user) {
            const params = new URLSearchParams()
            params.set('from', new URL(args.request.url).pathname)
            return redirect(`/sign-in?${params.toString()}`)
        }

        return await action(args, Clerk) as ReturnType<AuthenticatedAction>
    })
}

export const createLimitedAction = <
    LimitKey extends keyof UserLimits,
    LimitedAction extends LimitedActionFunction
>(
    config: LimitedActionConfig<LimitKey>,
    action: LimitedAction,
) => {
    return createAuthenticatedAction(async (args, Clerk) => {
        await refreshClerkUser(Clerk)
        const userLimits = parseUserLimits(Clerk.user?.publicMetadata?.limits)
        if (!userLimits[config.limit]) {
            return redirect(config.getRedirectPath(args, Clerk, userLimits))
        }

        return await action(args, Clerk, userLimits) as ReturnType<LimitedAction>
    })
}
// const example: ActionFunction<{}> = async ({ request }) => {
//     try {
//         const formData = await request.formData()
//         const title = formData.get("title"),
//             targetAmount = formData.get("targetAmount"),
//             organizer = JSON.parse(formData.get("organizer") as string)
//         const amount = toNum(`${targetAmount || ''}`)

//         console.log("Create Group Data:", { title, targetAmount, amount, organizer })
//         if (!isNonEmptyString(title) || !isNumber(amount) || !isObject<User>(organizer)) throw createResponseObj({
//             status: 'failed', type: 'local', name: 'missing-resources',
//             message: `Missing required resources. ${JSON.stringify({ title, amount, organizer })}`
//         })

//         setLoaderText("Generating Group")
//         var group = await VirtualCardAPI.createGroup({ title, targetAmount: amount, organizer })
//         group = await VirtualCardAPI.getGroup(group.id)
//         updateSavedGroups(group)

//         return redirect(`/group/${group.id}/pledge`)
//     } catch (error) {
//         console.error(error)
//         return json(error, { status: 500 })
//     }
// }
